import NiceModal from '@ebay/nice-modal-react';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Button from 'components/field/Button';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import Tabs from 'components/global/Tabs';
import Typography from 'components/global/Typography';

import useMediaQuery from 'hooks/useMediaQuery';

import { Breakpoints } from 'utils/const.styles';

import { faChevronDown, faChevronUp, faFire } from '@fortawesome/pro-regular-svg-icons';

import ConnectSalesChannelModal from './ConnectSalesChannelModal';
import * as S from './SalesChannelDetail.styled';
import SalesChannelGallery from './SalesChannelGallery';
import useStores from './Stores/useStores';

const HIDE_CONNECT_BUTTON_CHANNELS = ['zid', 'salla', 'bigcommerce'];

const SalesChannelDetailContent = () => {
	const [showFullDescription, setShowFullDescription] = useState(false);
	const isMobile = useMediaQuery(Breakpoints.mobile);
	const { t } = useTranslation();
	const { salesChannel } = useSelector((state) => state.salesChannel);

	const getFormattedDescription = () => {
		const description = t(`${salesChannel?.slug}.description`);
		if (isMobile && !showFullDescription && description.length > 150) {
			return `${description.slice(0, 150)}...`;
		}
		return description;
	};

	const highlights = useMemo(() => {
		const highlights = [];
		for (let i = 1; i <= 20; i += 1) {
			const highlightKey = `${salesChannel?.slug}.highlight${i}`;
			if (t(highlightKey) !== highlightKey) {
				highlights.push(highlightKey);
			} else {
				break;
			}
		}
		return highlights;
	}, [salesChannel]);

	const handleConnectStore = () => {
		NiceModal.show(ConnectSalesChannelModal);
	};

	const { tabs } = useStores();

	return (
		<S.SalesChannelCard>
			<S.TitleContainer>
				<Image url={salesChannel?.icon} width="7rem" height="7rem" />
				<div>
					<Typography variant="p1" fontWeight="700">
						{`salesChannel.${salesChannel?.integrationName}`}
					</Typography>
					<Typography variant="p3" noWrap fontWeight="500" color="var(--content-3)">
						{`${salesChannel?.slug}.subtitle`}
					</Typography>
				</div>
			</S.TitleContainer>
			<S.Content>
				<S.StoreInfo hasImage={salesChannel?.images?.length > 0}>
					{salesChannel?.images?.length > 0 && <SalesChannelGallery />}
					<S.StoreInfoContent>
						<Typography variant="p2" fontWeight="700">
							{t('salesChannelAbout', {
								channelName: t(`salesChannel.${salesChannel?.integrationName}`)
							})}
						</Typography>
						<S.StoreDescription>
							<Typography variant="p3" noWrap fontWeight="500" color="var(--content-3)">
								{getFormattedDescription()}
							</Typography>
							{isMobile && t(`${salesChannel?.slug}.description`).length > 150 && (
								<S.ButtonWrapper>
									<Button
										variant="text"
										size="small"
										icon={showFullDescription ? faChevronUp : faChevronDown}
										iconPos="right"
										label={showFullDescription ? 'showLess' : 'readMore'}
										onClick={() => setShowFullDescription(!showFullDescription)}
									/>
								</S.ButtonWrapper>
							)}
						</S.StoreDescription>
						{highlights.length > 0 && (
							<S.Highlights>
								<Typography variant="p2" fontWeight="700">
									highlights
								</Typography>
								<S.HighlightsList>
									{highlights.map((highlight) => (
										<S.HighlightContainer key={highlight}>
											<Icon icon={faFire} color="var(--content-2)" />
											<S.HighlightContent>
												<Typography variant="p3" fontWeight="500">
													{highlight}
												</Typography>
												<Typography variant="caption" color="var(--content-3)" noWrap>
													{`${highlight}.description`}
												</Typography>
											</S.HighlightContent>
										</S.HighlightContainer>
									))}
								</S.HighlightsList>
							</S.Highlights>
						)}
						{!HIDE_CONNECT_BUTTON_CHANNELS.includes(salesChannel?.slug) &&
							salesChannel?.images?.length > 0 && (
								<Button label="connectStore" onClick={handleConnectStore} fullWidth />
							)}
					</S.StoreInfoContent>
				</S.StoreInfo>
				<S.Separator />
				<S.InfoTabs>
					<Tabs data={tabs} />
				</S.InfoTabs>
			</S.Content>
		</S.SalesChannelCard>
	);
};

export default SalesChannelDetailContent;
